/**
 * Planning, building and editing — asked of the server.
 *
 * The pipeline that decides what an app becomes (the prompt, the gate, the repair loop,
 * the design kits, the runtime inlined into every published page) used to run in the
 * browser, which meant every visitor downloaded it and changing a sentence in a prompt was
 * a frontend deploy. It runs server-side now; this asks it to.
 *
 * These go to the build service's own origin, a Lambda Function URL, because a build takes
 * the better part of a minute and API Gateway stops at twenty-nine seconds.
 */
import type { AppPlan } from '@/webapp/plan/planTypes';
import type { AttachedText } from '@/webapp/prompt';
import type { StyleBrief } from '@/llm/planTypes';
import { ApiError, request } from './client';

/**
 * How long the build Lambda may run: `timeout` on `WebBuildHandler` in infra/lib/stack.ts.
 *
 * Copied, because the browser cannot import the CDK stack, and pinned by a test that reads
 * the stack and fails when the two disagree. This client aborted at 180s while the server ran
 * to 600s, so every build between the two was finished, charged, and reported here as a
 * failure — the phone had the same bug at 330s.
 */
export const BUILD_LAMBDA_TIMEOUT_MS = 600_000;

/**
 * Past the server's own limit, so giving up means there is genuinely no answer coming.
 *
 * Must stay above `timeout` on WebBuildHandler in infra/lib/stack.ts (the phone mirrors it as
 * BUILD_LAMBDA_TIMEOUT_MS in src/webapp/buildClient.ts). Exported because it is also the age
 * past which a build this browser sent cannot still be running somewhere (buildRun.recover).
 */
export const BUILD_TIMEOUT_MS = BUILD_LAMBDA_TIMEOUT_MS + 30_000;
/**
 * /plan, /edit and /assemble run on the SAME Lambda as /build, with the same 600s limit and a
 * model deadline of about the remaining time less 45s, and the model client retries inside
 * that deadline. So a plan or an edit can also run well past two minutes — and an edit is
 * charged before it runs, so aborting at 120s reported a paid, finished edit as a failure.
 */
const EDIT_TIMEOUT_MS = BUILD_LAMBDA_TIMEOUT_MS + 30_000;

/**
 * How often to ask again while the server says this intent is still running. Its rate limit
 * is 30 requests in five minutes per account; this spends at most fifteen of them in any five.
 */
const IN_PROGRESS_POLL_MS = 20_000;

export interface PlanResult {
  /** Always complete: the scaffold, if the model pass failed. */
  plan: AppPlan;
  refined: boolean;
}

/**
 * The server's plan for an idea.
 *
 * `media` is the NAMES of photos already attached (contract C2), so the plan can offer a photo
 * hero and a gallery when there are photos, unless the idea already covers them. Names only — the
 * plan decides what the app has, not what each picture shows. Sent only when there are some, so
 * a plan with nothing attached asks exactly what it always asked. The /plan handler live today
 * ignores the field (it calls planApp with the goal alone); it counts once webBuild.ts passes it
 * on, which is the same server change /build's descriptions and documents wait for.
 */
export async function planApp(goal: string, opts: { media?: string[] } = {}): Promise<PlanResult> {
  return request<PlanResult>('/plan', {
    base: 'build',
    method: 'POST',
    body: { goal, ...(opts.media?.length ? { media: opts.media } : {}) },
    timeoutMs: EDIT_TIMEOUT_MS,
  });
}

/**
 * A photo as the build store holds it (contract C2): its name, and the one-line description the
 * person saw under its thumbnail and may have edited — what will let the model pick the hero and
 * write real alt text for an IMG_1234.jpg instead of guessing from the file name (F9). A plain
 * name is still accepted, as a record from before descriptions has them.
 */
export type BuildMedia = string | { name: string; description?: string };

/**
 * What /build is sent for the photos: each name, with the caption the person kept (D2).
 *
 * This sent names only while the live /build passed `body.media` straight into the prompt, where
 * an object printed as '[object Object]' (review of lane/build-publish, 2026-09-30). The server now
 * reads media through readBuildAttachments (lane/prompt-server, merged beside this), which takes a
 * string or { name, description } and fences the description as the person's words. The server
 * deploys before the web, so no live builder sends objects to a server that cannot read them.
 * An empty or cleared caption is left out rather than sent as ''.
 */
function mediaForBuild(media: BuildMedia[]): BuildMedia[] {
  return media.map((m) => {
    if (typeof m === 'string') return m;
    const description = m.description?.trim();
    return description ? { name: m.name, description } : { name: m.name };
  });
}

export interface BuiltApp {
  appId: string;
  appName: string;
  /** The finished document, runtime already inlined. */
  html: string;
  appCode: string;
  attempts?: number;
  repairRounds?: number;
  gateFailures?: number;
}

export async function buildApp(input: {
  goal: string;
  appName?: string;
  vibe?: string | null;
  plan?: AppPlan | null;
  /*
   * The MANIFEST of an attached document, never the document.
   *
   * A book does not fit in a prompt, and a model that has seen the text will reproduce it
   * from memory rather than fetch it — so the generator is told what the text is called and
   * what its parts are, and writes an app that reads them with `S.asset(name, id)`. The text
   * itself goes to POST /apps/{appId}/text once the app exists.
   */
  textAsset?: AttachedText | null;
  /*
   * NAMES, not URLs. Nothing has been uploaded at this point — the app does not exist yet to
   * hang an upload on — so the app is written against `S.media(name)` and the addresses are
   * filled in when the page is served.
   */
  media?: BuildMedia[] | null;
  /**
   * Short text files the person said to put ON THE PAGE — a menu, a schedule, a price list
   * (owner decision D1, 2026-09-30). The text itself, unlike `textAsset`: it is small enough for
   * the prompt, and the model has to read it to lay it out.
   *
   * The /build handler live today does NOT read this field: it is neither limit-checked,
   * screened nor given to the model, so it cannot reach the page — and it must not until it is
   * screened. Sent anyway because an unread field changes nothing on that server, and the page
   * gets it the day webBuild.ts reads it: through readBuildAttachments (the /edit limits: 3
   * files, 20,000 characters each, 40,000 in all), its text added to what checkContentPolicy
   * reads before the charge, and handed to generateWebApp.
   */
  documents?: { name: string; text: string }[] | null;
  /** The feel of a reference picture. Colours are excluded by design; the kit owns those. */
  style?: StyleBrief | null;
  /**
   * One value per "build this app", reused by every retry of it.
   *
   * The server claims it before it charges, so a retry of the SAME intent is recognised and
   * costs nothing. Minting a fresh one per attempt would defeat that entirely — see
   * `state/buildIntent.ts`, which keeps one across `retry()` and a reload.
   */
  requestKey?: string;
}, options: { inProgressPollMs?: number; untilBack?: () => Promise<void>; reconnectPauseMs?: number } = {}): Promise<BuiltApp> {
  const { requestKey, media, documents, ...rest } = input;
  /*
   * Each list only when it has something in it: an empty one and an absent one are the same
   * build, and absent is what every server before contract C2 already understands.
   */
  const payload = {
    ...rest,
    ...(media?.length ? { media: mediaForBuild(media) } : {}),
    ...(documents?.length ? { documents } : {}),
  };
  const ask = (resume: boolean) =>
    request<BuiltApp>(resume ? '/build?resume=1' : '/build', {
      base: 'build',
      method: 'POST',
      body: payload,
      timeoutMs: BUILD_TIMEOUT_MS,
      // A header, because the server reads it before it parses anything else.
      ...(requestKey ? { headers: { 'Idempotency-Key': requestKey } } : {}),
    });
  if (!requestKey) return ask(false);
  return untilAnswered(ask, options);
}

/**
 * Ask under one key until the server ANSWERS — the app, or the failure it actually met.
 *
 * Two things are not answers, and neither is shown to the person:
 *
 * - A 409 `build_in_progress`: an earlier attempt of this SAME intent is still running (the tab
 *   was reloaded mid-build, or Try again arrived while the server worked on). The server's word
 *   for that is "wait, not pay", so wait and ask again until the build lands (a free replay) or
 *   its claim is released. Bounded by the ceiling, counted from the FIRST 409: anything running
 *   then ends within the Lambda's limit, so a key still claimed after that is stuck, not busy.
 *
 * - A connection that was LOST rather than answered. A phone that locks, or a tab sent to the
 *   background while the person looks at something else, drops the request; the server builds on
 *   regardless. This used to end on "We could not reach the server" and a Try again button for a
 *   build that was fine (owner, 2026-10-02). Now it waits until the page is back in front of the
 *   person and online, then asks with `?resume=1`: the server replays the finished app, says it is
 *   still building, or hands back the failure it recorded — and never starts a second, paid build
 *   because of a lost connection.
 */
async function untilAnswered<T>(
  ask: (resume: boolean) => Promise<T>,
  options: { inProgressPollMs?: number; untilBack?: () => Promise<void>; reconnectPauseMs?: number },
): Promise<T> {
  let giveUpAt: number | null = null;
  let lostSince: number | null = null;
  let resume = false;
  for (;;) {
    try {
      return await ask(resume);
    } catch (e) {
      if (isBuildInProgress(e)) {
        giveUpAt ??= Date.now() + BUILD_TIMEOUT_MS;
        if (Date.now() >= giveUpAt) throw e;
        await new Promise((r) => setTimeout(r, options.inProgressPollMs ?? IN_PROGRESS_POLL_MS));
        continue;
      }
      if (isConnectionLost(e)) {
        lostSince ??= Date.now();
        // Past the ceiling the server has long finished; a connection still failing is the network.
        if (Date.now() - lostSince > BUILD_TIMEOUT_MS) throw e;
        await (options.untilBack ?? untilBack)();
        await new Promise((r) => setTimeout(r, options.reconnectPauseMs ?? 1500));
        resume = true;
        continue;
      }
      throw e;
    }
  }
}

/** The request never got an answer — offline, or our own deadline — as opposed to a server reply. */
export function isConnectionLost(e: unknown): boolean {
  return e instanceof ApiError && (e.kind === 'offline' || e.kind === 'timeout');
}

/** Resolves once the page is visible and the browser says it is online. */
export function untilBack(): Promise<void> {
  const back = () =>
    (typeof document === 'undefined' || document.visibilityState === 'visible') &&
    (typeof navigator === 'undefined' || navigator.onLine !== false);
  if (back()) return Promise.resolve();
  return new Promise((resolve) => {
    const check = () => {
      if (!back()) return;
      document.removeEventListener('visibilitychange', check);
      window.removeEventListener('online', check);
      window.removeEventListener('pageshow', check);
      window.removeEventListener('focus', check);
      clearInterval(poll);
      resolve();
    };
    document.addEventListener('visibilitychange', check);
    window.addEventListener('online', check);
    window.addEventListener('pageshow', check);
    window.addEventListener('focus', check);
    // Some browsers restore a frozen page without firing any of these.
    const poll = setInterval(check, 2000);
  });
}

/** The server is still working on this key: the body's `error` is the code, on a 409. */
export function isBuildInProgress(e: unknown): boolean {
  return e instanceof ApiError && e.status === 409 && e.reason === 'build_in_progress';
}

export async function assembleApp(input: {
  appId: string;
  source: string;
  appName?: string;
  accent?: string;
  description?: string;
}): Promise<string> {
  const { html } = await request<{ html: string }>('/assemble', {
    base: 'build',
    method: 'POST',
    body: input,
    timeoutMs: EDIT_TIMEOUT_MS,
  });
  return html;
}

/**
 * An edit is /edit — and nothing else. The page it goes on is assembled by the publish that
 * follows it (`publishLatest`), from the source as SAVED, exactly as the phone does it
 * (`editOnServer`, then `hostLatest`).
 *
 * This used to call /assemble as well and hand back a document nobody read: chat publishes the
 * saved source, which assembles it again. So every edit paid a second round trip, a second
 * whole-document download and one more of the 30 requests the build service allows an account
 * in five minutes — and worse, an /assemble that failed there threw away an edit that had
 * ALREADY been paid for, where failing in the publish leaves it saved and the page behind.
 *
 * The appId is still required, and still checked before the paid call: it is the id the edit
 * will be published under — the localStorage namespace baked into the page — so an app with none
 * could be charged for a change that can never reach its page.
 */
export async function editApp(input: {
  appId: string;
  source: string;
  instruction: string;
  goal?: string;
  /**
   * The plan the app was built from, when the record has one.
   *
   * The phone has always sent it (`chatStore` → `editOnServer`), and the server gives it to the
   * edit as the app's INVARIANTS — its server-enforced rules, its capacity, the collection each
   * person keeps (`refineWebApp`). The web sent none, so an edit made here was free to return an
   * app that had quietly dropped a limit the build set. Absent — an app built before the record
   * kept a plan — the edit still runs, as it always has.
   */
  plan?: AppPlan | null;
  /**
   * One value per change asked for, reused when the SAME words are sent again for the SAME
   * source — the resend after an answer that never arrived. The phone sends one too
   * (`chatStore` → `editOnServer`): an edit is charged before it runs, so a lost answer resent
   * without it is a second, paid edit. A header, as on /build, and so already one the build
   * service's CORS lets a browser send.
   */
  requestKey?: string;
  /**
   * The NAMES of photos attached with this change, already uploaded as the app's creator assets.
   *
   * The owner's ask (2026-09-29): "add a couple of images to the site as carousel backgrounds"
   * from the chat. The model is given names, never URLs, and writes `S.media('beach.jpg')`; the
   * page is handed the addresses when it is served (site.ts, from the row's assets). The shared
   * contract for POST /edit — absent, or empty, is today's edit.
   */
  media?: string[];
  /** The names of photos the app already has (its definition's assets), so they can be used too. */
  existingMedia?: string[];
  /**
   * Text files attached with this change — a menu, a price list — read as UTF-8 here. The
   * server's limits (3 files, 20,000 characters each, 40,000 in all) are checked before sending,
   * and again by the server before it charges.
   */
  documents?: { name: string; text: string }[];
}, options: { inProgressPollMs?: number; untilBack?: () => Promise<void>; reconnectPauseMs?: number } = {}): Promise<{ appCode: string; drift?: { reasons: string[] } }> {
  if (!input.appId) {
    throw new ApiError(
      'misconfigured',
      'This app has no id to publish under — editing it would strand the data of anyone already using it.',
    );
  }

  const ask = (resume: boolean) => request<{
    appCode: string;
    attempts?: number;
    gateFailures?: number;
    /*
     * Set when the edit looks like it REPLACED the app — "add a phone number field" coming back
     * as a different product. The server reports rather than refuses, because only the person
     * who typed it knows what they meant; this client dropped it, so the swap went out
     * unremarked. Passed on for chat to say.
     */
    drift?: { reasons: string[] };
  }>(resume ? '/edit?resume=1' : '/edit', {
    base: 'build',
    method: 'POST',
    body: {
      source: input.source,
      instruction: input.instruction,
      ...(input.goal ? { goal: input.goal } : {}),
      ...(input.plan ? { plan: input.plan } : {}),
      // Only when there are some: an empty list and an absent one mean the same edit, and the
      // absent one is what every server before this contract already understands.
      ...(input.media?.length ? { media: input.media } : {}),
      ...(input.existingMedia?.length ? { existingMedia: input.existingMedia } : {}),
      ...(input.documents?.length ? { documents: input.documents } : {}),
    },
    timeoutMs: EDIT_TIMEOUT_MS,
    ...(input.requestKey ? { headers: { 'Idempotency-Key': input.requestKey } } : {}),
  });
  // Keyed, an edit rides out a lost connection the way a build does (`untilAnswered`).
  const { appCode, drift } = input.requestKey ? await untilAnswered(ask, options) : await ask(false);

  return { appCode, ...(drift?.reasons?.length ? { drift } : {}) };
}
